// ConfirmDeleteDialog.js
// Reusable confirmation dialog for delete operations with optional password requirement

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const ConfirmDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'אישור מחיקה',
  message = 'האם אתה בטוח שברצונך למחוק? פעולה זו אינה ניתנת לביטול.',
  requirePassword = true,
  confirmButtonText = 'מחיקה',
  cancelButtonText = 'ביטול',
}) => {
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when dialog closes
  const handleClose = () => {
    setStep(1);
    setPassword('');
    setPasswordVisible(false);
    setIsLoading(false);
    setError('');
    onClose();
  };

  // Step 1: Confirm the action
  const handleConfirmStep1 = () => {
    if (requirePassword) {
      setStep(2);
    } else {
      handleFinalConfirm();
    }
  };

  // Step 2 / Final: Execute the deletion
  const handleFinalConfirm = async () => {
    if (requirePassword && !password) {
      setError('נדרשת סיסמה למחיקה');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onConfirm(password);
      handleClose();
    } catch (err) {
      console.error('Delete error:', err);
      if (err?.response?.status === 401) {
        setError('סיסמה שגויה');
      } else {
        setError(
          err?.response?.data?.error ||
          err.message ||
          'שגיאה בעת ביצוע הפעולה'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
        {title}
      </DialogTitle>

      <DialogContent>
        {step === 1 ? (
          <Typography>{message}</Typography>
        ) : (
          <>
            <Typography sx={{ mb: 2 }}>
              יש להזין סיסמה לאישור המחיקה:
            </Typography>
            <TextField
              label="סיסמה"
              type={passwordVisible ? 'text' : 'password'}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2, direction: 'ltr' }}
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      edge="end"
                    >
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isLoading}
          sx={{ fontSize: '1rem' }}
        >
          {cancelButtonText}
        </Button>

        {step === 1 ? (
          <Button
            onClick={handleConfirmStep1}
            variant="contained"
            color="error"
            disabled={isLoading}
            sx={{ fontSize: '1rem' }}
          >
            {isLoading ? <CircularProgress size={24} /> : (requirePassword ? 'המשך' : confirmButtonText)}
          </Button>
        ) : (
          <Button
            onClick={handleFinalConfirm}
            variant="contained"
            color="error"
            disabled={isLoading || (requirePassword && !password)}
            sx={{ fontSize: '1rem' }}
          >
            {isLoading ? <CircularProgress size={24} /> : confirmButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
